import { Button, Card, CardContent, Grid, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import COVERAGE_CATEGORY from '../../../enums/coverageCategories';
import { ReturnOrderDialog } from '../../ReturnOrderDialog';
import FormDefinitionInput from '../FormDefinitionInput';
import { getAnswerImages } from '../FormDefinitionInput/FormDefinitionInput.utils';
import { OrderReviewContext } from '../OrderReviewContext';
import styles from './FormDefinition.styles';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { useLocation } from 'react-router-dom';

const propTypes = {
  classes: PropTypes.object.isRequired,
  duplicateQuestion: PropTypes.bool,
  formDefinition: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  imageToEdit: PropTypes.object,
  removeAnswers: PropTypes.func.isRequired,
  saveAnswers: PropTypes.func.isRequired,
  showTitle: PropTypes.bool,
  showAsTableRow: PropTypes.bool,
};

const defaultProps = {
  duplicateQuestion: false,
  isReadOnly: false,
  imageToEdit: null,
  showTitle: true,
  showAsTableRow: false,
  imageTag: false
};

/** True if this node or any nested `children` (from QA tree build) has saved answers. */
const subtreeHasSavedAnswers = node => {
  if ((node.answers?.length ?? 0) > 0) return true;
  return (node.children ?? []).some(subtreeHasSavedAnswers);
};

const FormDefinition = ({
  classes,
  duplicateQuestion = false,
  formDefinition,
  isReadOnly = false,
  imageToEdit = null,
  removeAnswers,
  saveAnswers,
  showTitle = true,
  showAsTableRow = false,
  showMinimalPhotoTaggedButton = false,
  customTitle,
  inputStyle,
  imageTag,
}) => {
  const [addSupplementDialogContent, setAddSupplementDialogContent] = useState({
    open: false,
    supplementName: '',
    formHeaderId: null,
    loading: false,
  });
  const root = React.useRef();
  const { dispatch: orderReviewDispatch, state, actions } = useContext(OrderReviewContext);
  const formHeaderName = state.formHeaders.find(fh => fh.formHeaderId === formDefinition.formHeaderId).name;
  const [showReturnOrderDialog, setShowReturnOrderDialog] = useState(false);
  const [returnOrderReason, setReturnOrderReason] = useState('');
  const answerImages = getAnswerImages(formDefinition);

  // Hide Questions from Review and Qa View
  const location = useLocation();
  const isFieldRepReviewView = location.pathname.includes('/review');
  const isQaView = location.pathname.includes('/qa');

  // Hide Non Applicable and QA Hidden Fields
  if (isFieldRepReviewView && (formDefinition.qaFieldContext === 'Hidden' || formDefinition.fieldContext === 'Not Applicable')) {
    return null;
  }

  if (isQaView && formDefinition.qaFieldContext === 'Hidden') {
    return null
  }

  // 2/15/2024 - Put into transform Hazard Score RO inputs to textfields
  // If the formDefinition is read only, then alter the answer to a text field and set the text to the value
  const alterFormDefinition = (formDefinition) => {
    if (formDefinition?.fieldContext !== "Read Only") return false;
    formDefinition.answerType = "TextBox";
    if (formDefinition?.answers?.length > 0 && formDefinition.answers[0].text === null) {
      formDefinition.answers[0].text = formDefinition.answers[0].value;
    }
  }
  alterFormDefinition(formDefinition);

  const togglePhoto = () => {
    if (formDefinition.answers.length === 1) {
      const answer = formDefinition.answers[0];
      if (answer.images) {
        let photoExists = answer.images.some(i => i.formQaImageId === imageToEdit.formQaImageId);
        if (photoExists) {
          answer.images = answer.images.filter(i => i.formQaImageId !== imageToEdit.formQaImageId);
        } else {
          answer.images = answer.images.concat(imageToEdit);
        }
      }
      updateFormHeaders([answer]);
      saveAnswers([answer]);
    } else if (formDefinition.answers.length > 1) {
      let answeredQuestions = formDefinition.answers.filter(fda => fda.images.length > 0);
      formDefinition.answers.forEach(a => {
        if (a.images) {
          let photoExists = answeredQuestions.length > 0;
          if (photoExists) {
            a.images = a.images.filter(i => i.formQaImageId !== imageToEdit.formQaImageId);
          } else {
            a.images = a.images.concat(imageToEdit);
          }
        }
      });
      updateFormHeaders(formDefinition.answers);
      saveAnswers(formDefinition.answers);
    }
  };

  // const doesTextBoxHaveAnswerInForm =
  //   formDefinition.answerType === 'TextBox' && formDefinition.answers.some(fa => fa.text === null || fa.text === '');

  const isImageToEditPhotoTagged = imageToEdit
    ? formDefinition.answers.some(a => a.images.some(i => i.formQaImageId === imageToEdit.formQaImageId))
    : false;

  const isHeader = formDefinition.answerType === 'Header';

  const showPhotoToggleButton =
    imageToEdit &&
    !isReadOnly &&
    !isHeader &&
    formDefinition.answerType !== 'Read Only' &&
    formDefinition.answers.length > 0;

  const confirmAddSupplement = async () => {
    setAddSupplementDialogContent({
      ...addSupplementDialogContent,
      loading: true,
    });
    await actions.addSupplementAsync(addSupplementDialogContent.formHeaderId);
    setAddSupplementDialogContent({
      ...addSupplementDialogContent,
      show: false,
    });
  };

  const checkIfAnswerTriggersNewFormHeader = async newAnswerValueId => {
    const answerTrigger = formDefinition.answerTriggers.find(
      answerTrigger => newAnswerValueId === answerTrigger.answerValueId && answerTrigger.triggerToFormHeaderId,
    );
    const existingFormHeader = state.formHeaders.find(
      fh => answerTrigger && fh.formHeaderId === answerTrigger.triggerToFormHeaderId,
    );

    const canAddNewFormHeader =
      !existingFormHeader || existingFormHeader.coverageCategoryId === COVERAGE_CATEGORY.SUPPLEMENTAL_MULTIPLE;

    if (!!answerTrigger && canAddNewFormHeader) {
      const formHeader = state.orderableFormHeaders.find(ofh => ofh.id === answerTrigger.triggerToFormHeaderId);

      setAddSupplementDialogContent({
        ...addSupplementDialogContent,
        show: true,
        supplementName: !!formHeader ? formHeader.name : '',
        formHeaderId: answerTrigger.triggerToFormHeaderId,
      });

      // setShowAddFormDialog(formHeader ? formHeader.name : true);
      // await actions.addSupplementAsync(answerTrigger.triggerToFormHeaderId);
      // setShowAddFormDialog(false);
    }
  };

  const updateFormHeaders = newAnswers => {
    orderReviewDispatch({
      type: 'UPDATE_ANSWERS',
      questionId: formDefinition.questionId,
      newAnswers: newAnswers,
      orderFormHeaderId: formDefinition.orderFormHeaderId,
    });
  };

  const renderPhotoToggleButton = ({ minimal = false } = {}) => {
    const buttonLabel = isImageToEditPhotoTagged
      ? minimal
        ? 'Remove'
        : 'Remove from Photo'
      : minimal
      ? 'Add'
      : 'Add to Photo';

    if (formDefinition.answers[0].value !== 'No' && formDefinition.answers[0].text !== '') {
      return (
        <Button
          // color={isImageToEditPhotoTagged ? 'secondary' : 'var(--jmi-blue)'}
          // variant="contained"
          onClick={togglePhoto}
          className={classNames(classes.photoButton, isImageToEditPhotoTagged && classes.removeButton)}
          focusVisibleClassName={isImageToEditPhotoTagged && classes.rejectButtonFocused}
          fullWidth
          style={minimal ? { marginTop: 0 } : {}}
        >
          {isImageToEditPhotoTagged ? (
            <RemoveIcon className={classes.leftIcon} />
          ) : (
            <AddIcon className={classes.leftIcon} />
          )}
          {buttonLabel}
        </Button>
      );
    }
  };

  const renderTitle = ({ showDuplicateQuestion = false } = {}) => {
    if (!showTitle) {
      return null;
    }

    // hiding this label because the 'Calculate Valuation' button is also hidden `ValuationCalculation` component
    if (formDefinition.questionValue === 'Calculate' && state.isFieldRep) {
      return null;
    }

    // Section header: question text only, styled as a header
    if (isHeader) {
      return (
        <Typography variant="h6" style={{ fontWeight: 600, textDecoration: 'underline' }}>
          {formDefinition.questionValue}
        </Typography>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div className={classes.questionValueWrapper}>
          <Typography
            variant="subtitle1"
            classes={{
              subtitle1: classes.subtitle1,
            }}
          >
            {/* <CameraIcon style={{ marginRight: 8 }} /> */}
            {formDefinition.required && (
              <span
                style={{
                  color: 'red',
                }}
              >
                *
              </span>
            )}
            {formDefinition.questionValue}
          </Typography>
          {showDuplicateQuestion && duplicateQuestion && <Typography variant="caption">{formHeaderName}</Typography>}
        </div>

        {showPhotoToggleButton && showMinimalPhotoTaggedButton && (
          <div style={{ paddingLeft: 12, marginRight: 18 }}>{renderPhotoToggleButton({ minimal: true })}</div>
        )}
      </div>
    );
  };

  return (
    <>
      {formDefinition.answerType !== 'Picture' && (
        <Card
          ref={root}
          id={`${formDefinition.formDefinitionId}`}
          elevation={0}
          className={classNames(
            classes.card,
            answerImages.length === 0 && classes.noMinHeight,
            showAsTableRow && classes.showAsTableRowCard,
          )}
          classes={{
            root: classes.overflowVisible,
          }}
        >
          <CardContent
            className={classNames(
              classes.cardContent,
              imageToEdit && !isReadOnly && classes.removeBottomPadding,
              showAsTableRow && classes.removeBottomPadding,
            )}
            classes={{
              root: classes.cardContentRoot,
            }}
          >
            {showAsTableRow ? (
              <Grid container direction="row" className={classes.tableRow}>
                <Grid item xs={9}>
                  {renderTitle()}
                </Grid>
                <Grid item xs={3}>
                  <div className={classes.inputWrapper}>
                    <FormDefinitionInput
                      checkIfAnswerTriggersNewFormHeader={checkIfAnswerTriggersNewFormHeader}
                      formDefinition={formDefinition}
                      isReadOnly={isReadOnly}
                      imageToEdit={imageToEdit}
                      removeAnswers={removeAnswers}
                      saveAnswers={saveAnswers}
                      updateFormHeaders={updateFormHeaders}
                      inputStyle={inputStyle}
                    />
                  </div>
                </Grid>
              </Grid>
            ) : (
              <React.Fragment>
                {renderTitle({
                  showDuplicateQuestion: true,
                })}
                <div className={classes.inputWrapper}>
                  <FormDefinitionInput
                    checkIfAnswerTriggersNewFormHeader={checkIfAnswerTriggersNewFormHeader}
                    formDefinition={formDefinition}
                    isReadOnly={isReadOnly}
                    imageToEdit={imageToEdit}
                    removeAnswers={removeAnswers}
                    saveAnswers={saveAnswers}
                    updateFormHeaders={updateFormHeaders}
                    inputStyle={inputStyle}
                  />
                </div>
              </React.Fragment>
            )}
            {showPhotoToggleButton && !showMinimalPhotoTaggedButton && renderPhotoToggleButton()}
            {showReturnOrderDialog && (
              <ReturnOrderDialog open orderLineId={state.orderLineId} defaultReason={returnOrderReason} />
            )}
            <div
              style={{
                marginLeft: formDefinition.required ? 48 : 0,
              }}
            >
              {!imageTag && formDefinition.children
                ?.filter((cfd) => {
                  const answerTriggers = formDefinition.answerTriggers ?? [];
                  let a = answerTriggers.some(
                    (at) =>
                      formDefinition.answers.some((a) => a.valueId == at.answerValueId) &&
                      at.triggerToFormDefinitionId == cfd.formDefinitionId,
                  );

                  let c = answerTriggers.every(
                    (at) =>
                      at.triggerToFormDefinitionId == answerTriggers[0]?.triggerToFormDefinitionId,
                  );

                  let d =
                    (cfd.answers?.length ?? 0) > 0 ||
                    subtreeHasSavedAnswers(cfd); // e.g. 27719 empty but 27720 answered — still show chain for QA
                  let logic = a || c || d;

                  return logic;
                })
                .map((cfd) => (
                  <div
                    key={`children-${cfd.formDefinitionId}`}
                    style={{
                      marginTop: 16,
                      marginLeft: formDefinition.children?.length === 1 ? 0 : 48,
                    }}
                  >
                    <FormDefinition
                      classes={classes}
                      formDefinition={cfd}
                      saveAnswers={saveAnswers}
                      removeAnswers={removeAnswers}
                      isReadOnly={isReadOnly}
                      imageToEdit={imageToEdit}
                      showTitle={showTitle}
                      duplicateQuestion={duplicateQuestion}
                      showAsTableRow={showAsTableRow}
                    />
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      <ConfirmationDialog
        visible={addSupplementDialogContent.show}
        isSubmitting={addSupplementDialogContent.loading}
        minWidth={10}
        minHeight={10}
        title={`New supplement available${!!addSupplementDialogContent.supplementName &&
          `: ${addSupplementDialogContent.supplementName}`}`}
        cancelButton={
          <Button
            variant="contained"
            onClick={() => {
              setAddSupplementDialogContent(prev => ({
                ...prev,
                show: false,
              }));
            }}
            className={classNames('red-button')}
            disabled={addSupplementDialogContent.loading}
          >
            Skip it
          </Button>
        }
        acceptButton={
          <Button
            variant="contained"
            onClick={confirmAddSupplement}
            className={classNames('light-blue-background')}
            disabled={addSupplementDialogContent.loading}
          >
            Add it
          </Button>
        }
      />
    </>
  );
};

FormDefinition.propTypes = propTypes;
FormDefinition.defaultProps = defaultProps;

export default withStyles(styles)(FormDefinition);
